import { Route, Routes } from '@angular/router';
import { authGuard } from './guard/auth.guard';

type ComponentLoader = NonNullable<Route['loadComponent']>;

const protectedRoute = (path: string, loader: ComponentLoader): Route => ({
  path,
  loadComponent: loader,
  canActivate: [authGuard],
});

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./pages/home/home.component').then((m) => m.HomeComponent),
  },
  {
    path: 'login',
    loadComponent: () => import('./compoenent/auth/login/login.component').then((m) => m.LoginComponent),
  },
  protectedRoute('dashboard', () =>
    import('./pages/dashboard-v2/dashboard-v2.component').then((m) => m.DashboardV2Component)),
  protectedRoute('get-started', () =>
    import('./pages/get-started/get-started.component').then((m) => m.GetStartedComponent)),
  protectedRoute('how-it-works', () =>
    import('./pages/how-it-works/how-it-works.component').then((m) => m.HowItWorksComponent)),
  protectedRoute('design-system', () =>
    import('./pages/design-system/design-system.component').then((m) => m.DesignSystemComponent)),
  {
    path: 'otp-verification',
    loadComponent: () => import('./compoenent/auth/otp/otp.component').then((m) => m.OtpComponent),
  },
  {
    path: 'forgot-password',
    loadComponent: () => import('./compoenent/auth/forgot-password/forgot-password.component').then((m) => m.ForgotPasswordComponent),
  },
  protectedRoute('sign-up', () =>
    import('./compoenent/auth/sign-up/sign-up.component').then((m) => m.SignUpComponent)),
  {
    path: 'signup-success',
    loadComponent: () => import('./compoenent/auth/signup-success/signup-success.component').then((m) => m.SignupSuccessComponent),
  },
  protectedRoute('members', () =>
    import('./pages/members/members.component').then((m) => m.MembersComponent)),
  protectedRoute('customer-analytics', () =>
    import('./pages/members/customer-analytics/customer-analytics.component').then((m) => m.CustomerAnalyticsComponent)),
  protectedRoute('analytics', () =>
    import('./pages/grow/analytics/grow-analytics.component').then((m) => m.GrowAnalyticsComponent)),
  protectedRoute('analytics/retention', () =>
    import('./pages/grow/analytics/retention/retention-dashboard.component').then((m) => m.RetentionDashboardComponent)),
  protectedRoute('products', () =>
    import('./pages/products/products.component').then((m) => m.ProductsComponent)),
  protectedRoute('segments', () =>
    import('./pages/segments/segments.component').then((m) => m.SegmentsComponent)),
  protectedRoute('loyalty-programs', () =>
    import('./pages/engage/loyalty/loyalty.component').then((m) => m.LoyaltyComponent)),
  protectedRoute('loyalty/create', () =>
    import('./pages/loyalty-programs/loyalty-wizard/loyalty-wizard.component').then((m) => m.LoyaltyWizardComponent)),
  protectedRoute('tiers', () =>
    import('./pages/engage/tiers/tiers.component').then((m) => m.TiersComponent)),
  protectedRoute('rules', () =>
    import('./pages/engage/automations/automations.component').then((m) => m.AutomationsComponent)),
  protectedRoute('policies', () =>
    import('./pages/engage/policies/policies.component').then((m) => m.PoliciesComponent)),
  protectedRoute('coupon', () =>
    import('./pages/engage/coupons/coupons.component').then((m) => m.CouponsComponent)),
  protectedRoute('qr-codes', () =>
    import('./pages/engage/qr-codes/qr-codes.component').then((m) => m.QrCodesComponent)),
  protectedRoute('payouts', () =>
    import('./pages/engage/payout-review/payout-review.component').then((m) => m.PayoutReviewComponent)),
  protectedRoute('member-portal', () =>
    import('./pages/engage/member-portal/member-portal.component').then((m) => m.MemberPortalComponent)),
  protectedRoute('member-portal/studio', () =>
    import('./pages/engage/portal-studio/portal-studio.component').then((m) => m.PortalStudioComponent)),
  protectedRoute('member-portal/engagement', () =>
    import('./pages/engage/member-portal/portal-engagement.component').then((m) => m.PortalEngagementComponent)),
  protectedRoute('announcements', () =>
    import('./pages/engage/announcements/announcements.component').then((m) => m.AnnouncementsComponent)),
  protectedRoute('faq', () =>
    import('./pages/engage/faqs/faqs.component').then((m) => m.FaqsComponent)),
  protectedRoute('festival-calendar', () =>
    import('./pages/engage/festival-calendar/festival-calendar.component').then(
      (m) => m.FestivalCalendarComponent,
    )),
  protectedRoute('journeys', () =>
    import('./pages/engage/journeys/journeys.component').then((m) => m.JourneysComponent)),
  protectedRoute('journeys/:id', () =>
    import('./pages/engage/journeys/journey-builder/journey-builder.component').then((m) => m.JourneyBuilderComponent)),
  protectedRoute('referral', () =>
    import('./pages/grow/referrals/referrals.component').then((m) => m.ReferralsComponent)),
  protectedRoute('redemption', () =>
    import('./pages/redemption/redemption.component').then((m) => m.RedemptionComponent)),
  protectedRoute('rewards', () =>
    import('./pages/engage/rewards/rewards.component').then((m) => m.RewardsComponent)),
  protectedRoute('integration', () =>
    import('./pages/integration/integration.component').then((m) => m.IntegrationComponent)),
  protectedRoute('transactions', () =>
    import('./pages/transactions/transactions.component').then((m) => m.TransactionsComponent)),
  {
    path: 'transactions/quick-bill',
    loadComponent: () =>
      import('./pages/transactions/transactions.component').then((m) => m.TransactionsComponent),
    canActivate: [authGuard],
    data: { posPage: true },
  },
  protectedRoute('feedback', () =>
    import('./pages/feedback/feedback.component').then((m) => m.FeedbackComponent)),
  protectedRoute('feedback/builder', () =>
    import('./pages/feedback/build-form/build-form.component').then((m) => m.BuildFormComponent)),
  protectedRoute('feedback/responses', () =>
    import('./pages/feedback/responses-form/responses-form.component').then((m) => m.ResponsesFormComponent)),
  protectedRoute('campaigns', () =>
    import('./pages/grow/campaigns/campaigns.component').then((m) => m.CampaignsComponent)),
  protectedRoute('billing', () =>
    import('./pages/billing/billing.component').then((m) => m.BillingComponent)),
  protectedRoute('api-webhooks', () =>
    import('./pages/api-webhooks/api-webhooks.component').then((m) => m.ApiWebhooksComponent)),
  protectedRoute('events', () =>
    import('./pages/connect/events/events.component').then((m) => m.EventsComponent)),
  protectedRoute('channels', () =>
    import('./pages/connect/channels/channels.component').then((m) => m.ChannelsComponent)),
  { path: 'settings', redirectTo: 'settings/business', pathMatch: 'full' },
  protectedRoute('settings/business', () =>
    import('./pages/settings/pages/settings-business.page').then((m) => m.SettingsBusinessPageComponent)),
  protectedRoute('settings/team', () =>
    import('./pages/settings/pages/settings-team.page').then((m) => m.SettingsTeamPageComponent)),
  protectedRoute('settings/audit', () =>
    import('./pages/settings/pages/settings-audit.page').then((m) => m.SettingsAuditPageComponent)),
  protectedRoute('settings/outlets', () =>
    import('./pages/settings/pages/settings-outlets.page').then((m) => m.SettingsOutletsPageComponent)),
  protectedRoute('settings/profile', () =>
    import('./pages/settings/pages/settings-profile.page').then((m) => m.SettingsProfilePageComponent)),
  protectedRoute('settings/custom-fields', () =>
    import('./pages/settings/pages/settings-custom-fields.page').then((m) => m.SettingsCustomFieldsPageComponent)),
  protectedRoute('settings/media', () =>
    import('./pages/settings/media/media.component').then((m) => m.MediaComponent)),
  protectedRoute('settings/setup', () =>
    import('./pages/settings/pages/settings-setup.page').then((m) => m.SettingsSetupPageComponent)),
  { path: 'mobile-setting', redirectTo: 'settings/business', pathMatch: 'full' },
  protectedRoute('mobile-settings/:id', () =>
    import('./compoenent/mobile/mobile-settings/mobile-settings.component').then((m) => m.MobileSettingsComponent)),
  { path: '**', redirectTo: '/login' },
];
